import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { PlusIcon, MinusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "../../utils/currency";
import { computeDisplayedOrderTotals, type OrderFormDiscountType } from "../../utils/orderTotals";
import { NumericInput } from "../atoms/NumericInput";
import type { OrderDiscountScope } from "../../types/models";

export type QtyMode = "units" | "packs";

/** Client-stable identity for a cart line (allows multiple lines with the same catalog_item_id). */
export function createOrderLineKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `line-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export interface OrderLineItemRow {
  /** Unique per row in the form; not sent to the API. */
  lineKey: string;
  catalog_item_id: string;
  name: string;
  unit_price: number;
  quantity: number;
  available?: number | null;
  sales_pack_sizes?: number[] | null;
  selectedPackSize?: number;
  qtyMode?: QtyMode;
  /** Product unit of measure (e.g. "bottle", "pcs") — makes the per-unit price explicit. */
  unit_of_measure?: string | null;
  /** Per-line discount (only used when discountScope === 'line'). */
  discount_type?: OrderFormDiscountType | null;
  discount_value?: number | null;
}

/** Explicit "the price is per underlying unit" hint, with the pack-equivalent for pack products. */
export function PriceUnitHint({ line }: { line: OrderLineItemRow }) {
  const { t } = useTranslation("orders");
  const unit = (line.unit_of_measure ?? "").trim() || t("form_unit_generic");
  const sizes = line.sales_pack_sizes ?? [];
  const packSize =
    line.qtyMode === "packs" ? (line.selectedPackSize ?? sizes[0]) : sizes[0];
  const showPack = sizes.length >= 1 && !!packSize && packSize > 1;
  return (
    <span className="block text-xs text-neutral-500 dark:text-neutral-400">
      {t("form_price_per_unit", { unit })}
      {showPack
        ? ` · ${t("form_price_per_pack", {
            size: packSize,
            price: formatCurrency((line.unit_price || 0) * packSize),
          })}`
        : ""}
    </span>
  );
}

/** Net line total (gross − line discount) for the current discount scope. */
function lineNetTotal(line: OrderLineItemRow, discountScope: OrderDiscountScope): number {
  return computeDisplayedOrderTotals([line], discountScope, "none", 0).lineTotals[0];
}

/** Compact per-line discount control (type toggle —/%/fixed + value). Only shown in 'line' mode. */
export function LineDiscountControl({
  line,
  setLineDiscountType,
  setLineDiscountValue,
}: {
  line: OrderLineItemRow;
  setLineDiscountType: (lineKey: string, type: OrderFormDiscountType) => void;
  setLineDiscountValue: (lineKey: string, value: number) => void;
}) {
  const { t } = useTranslation("orders");
  const type = (line.discount_type ?? "none") as OrderFormDiscountType;
  const options: { value: OrderFormDiscountType; label: string }[] = [
    { value: "none", label: t("form_discount_mode_none") },
    { value: "percentage", label: t("form_discount_type_percentage") },
    { value: "fixed", label: t("form_discount_type_fixed") },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="flex rounded-lg overflow-hidden border border-neutral-300 dark:border-neutral-600">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => setLineDiscountType(line.lineKey, o.value)}
            className={`px-2.5 py-2 text-xs font-medium min-h-[40px] border-l first:border-l-0 border-neutral-300 dark:border-neutral-600 ${
              type === o.value
                ? "bg-primary-600 text-white"
                : "bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-600"
            }`}
            aria-pressed={type === o.value}
            aria-label={`${t("form_col_discount")} ${o.label} — ${line.name}`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {type !== "none" && (
        <NumericInput
          min={0}
          step="0.01"
          value={line.discount_value ?? 0}
          onValueChange={(v) => setLineDiscountValue(line.lineKey, Math.max(0, v))}
          className="w-20 text-center py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 min-h-[40px]"
          aria-label={`${t("form_col_discount")} — ${line.name}`}
        />
      )}
    </div>
  );
}

/** Inline editable unit price (per underlying unit). */
export function UnitPriceInput({
  line,
  setUnitPrice,
}: {
  line: OrderLineItemRow;
  setUnitPrice: (lineKey: string, value: number) => void;
}) {
  const { t } = useTranslation("orders");
  return (
    <NumericInput
      min={0}
      step="0.01"
      value={line.unit_price}
      onValueChange={(v) => setUnitPrice(line.lineKey, Math.max(0, v))}
      className="w-24 text-center py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 min-h-[40px]"
      aria-label={`${t("form_col_unit_price")} — ${line.name}`}
    />
  );
}

/**
 * Quantity input with its own text buffer so clearing the field (to type a new number) shows
 * blank instead of being treated as "quantity 0" — which used to make the whole line disappear
 * (0 previously deleted the line; see setQuantity in Create/EditOrderForm). Only commits a change
 * once the typed value is a valid positive integer; blur reverts an incomplete/invalid entry back
 * to the last committed value.
 */
function QuantityInput({
  value,
  onCommit,
  ...rest
}: {
  value: number;
  onCommit: (value: number) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type">) {
  const [text, setText] = useState(() => String(value));

  useEffect(() => {
    if (text.trim() === "") return; // mid-edit (blank) — leave the buffer alone
    const parsed = parseInt(text, 10);
    if (!Number.isFinite(parsed) || parsed !== value) {
      setText(String(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <input
      {...rest}
      type="number"
      value={text}
      onFocus={(e) => {
        e.target.select();
        rest.onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value;
        setText(raw);
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n > 0) onCommit(n);
      }}
      onBlur={(e) => {
        const n = parseInt(text, 10);
        if (text.trim() === "" || !Number.isFinite(n) || n <= 0) {
          setText(String(value));
        }
        rest.onBlur?.(e);
      }}
    />
  );
}

export function LineItemQtyWithUnitsPacks({
  line,
  setQuantity,
  setLineQtyMode,
  setSelectedPackSize,
  updateQuantity,
}: {
  line: OrderLineItemRow;
  setQuantity: (lineKey: string, value: number) => void;
  setLineQtyMode: (lineKey: string, mode: QtyMode) => void;
  setSelectedPackSize: (lineKey: string, size: number) => void;
  updateQuantity: (lineKey: string, delta: number) => void;
}) {
  const { t } = useTranslation("orders");
  const sizes = line.sales_pack_sizes ?? [];
  const packSize =
    line.qtyMode === "packs"
      ? (line.selectedPackSize ?? sizes[0] ?? 1)
      : (sizes[0] ?? 1);
  const mode = line.qtyMode ?? "units";
  const displayValue = mode === "packs" ? Math.floor(line.quantity / packSize) : line.quantity;
  const delta = mode === "packs" ? packSize : 1;

  const handleCommit = (raw: number) => {
    const units = mode === "packs" ? raw * packSize : raw;
    setQuantity(line.lineKey, units);
  };

  const packs = mode === "packs" && packSize > 0 ? Math.floor(line.quantity / packSize) : 0;
  const isMultipleOfPack = line.quantity > 0 && packSize > 0 && line.quantity % packSize === 0;

  return (
    <div className="flex flex-col gap-2.5 p-3 rounded-lg border border-neutral-200 dark:border-neutral-600 bg-neutral-50/80 dark:bg-neutral-800/50 w-full min-w-0 md:min-w-[200px]">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 shrink-0">
          {t("form_qty_in")}
        </span>
        <div className="flex rounded-lg overflow-hidden border border-neutral-300 dark:border-neutral-600">
          <button
            type="button"
            onClick={() => setLineQtyMode(line.lineKey, "units")}
            className={`px-3 py-2 text-sm font-medium min-h-[40px] ${
              mode === "units"
                ? "bg-primary-600 text-white border-primary-600"
                : "bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-600 border-transparent"
            }`}
          >
            {t("form_bottles")}
          </button>
          <button
            type="button"
            onClick={() => setLineQtyMode(line.lineKey, "packs")}
            className={`px-3 py-2 text-sm font-medium min-h-[40px] border-l border-neutral-300 dark:border-neutral-600 ${
              mode === "packs"
                ? "bg-primary-600 text-white border-primary-600"
                : "bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-600 border-transparent"
            }`}
          >
            {t("form_packs")}
          </button>
        </div>
      </div>
      {mode === "packs" && sizes.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 shrink-0">
            {t("form_pack_size")}
          </span>
          {sizes.map((sz) => (
            <button
              key={sz}
              type="button"
              onClick={() => setSelectedPackSize(line.lineKey, sz)}
              className={`px-3 py-2 text-sm font-medium rounded-md min-h-[36px] ${
                (line.selectedPackSize ?? sizes[0]) === sz
                  ? "bg-primary-600 text-white border border-primary-600"
                  : "border border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600"
              }`}
            >
              {t("form_per_pack", { count: sz })}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => updateQuantity(line.lineKey, -delta)}
          className="p-2.5 rounded-lg border border-neutral-300 dark:border-neutral-600 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-neutral-600"
          aria-label={`${t("form_decrease_qty")} — ${line.name}`}
        >
          <MinusIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
        </button>
        <QuantityInput
          min={1}
          value={displayValue}
          onCommit={handleCommit}
          className="w-16 text-center py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 font-medium text-base min-h-[44px]"
          aria-label={`${t("form_col_qty")} — ${line.name}`}
        />
        <button
          type="button"
          onClick={() => updateQuantity(line.lineKey, delta)}
          className="p-2.5 rounded-lg border border-neutral-300 dark:border-neutral-600 min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-neutral-100 dark:hover:bg-neutral-600"
          aria-label={`${t("form_increase_qty")} — ${line.name}`}
        >
          <PlusIcon className="w-5 h-5 text-neutral-700 dark:text-neutral-300" />
        </button>
      </div>
      {(mode === "packs" && isMultipleOfPack) || (mode === "units" && line.quantity > 0) ? (
        <p className="text-sm text-neutral-600 dark:text-neutral-400 font-medium">
          {mode === "packs" && isMultipleOfPack
            ? t("form_packs_summary", { count: packs, packSize, total: line.quantity })
            : t("form_bottles_summary", { count: line.quantity })}
        </p>
      ) : null}
    </div>
  );
}

function SimpleQtyControls({
  line,
  setQuantity,
  updateQuantity,
}: {
  line: OrderLineItemRow;
  setQuantity: (lineKey: string, value: number) => void;
  updateQuantity: (lineKey: string, delta: number) => void;
}) {
  const { t } = useTranslation("orders");
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => updateQuantity(line.lineKey, -1)}
        className="p-2 rounded border border-neutral-300 dark:border-neutral-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label={`${t("form_decrease_qty")} — ${line.name}`}
      >
        <MinusIcon className="w-4 h-4" />
      </button>
      <QuantityInput
        min={1}
        value={line.quantity}
        onCommit={(n) => setQuantity(line.lineKey, n)}
        className="w-16 text-center py-2 border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-700 min-h-[44px]"
        aria-label={`${t("form_col_qty")} — ${line.name}`}
      />
      <button
        type="button"
        onClick={() => updateQuantity(line.lineKey, 1)}
        className="p-2 rounded border border-neutral-300 dark:border-neutral-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
        aria-label={`${t("form_increase_qty")} — ${line.name}`}
      >
        <PlusIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

export interface OrderLineItemsProps {
  lineItems: OrderLineItemRow[];
  orderTotal: number;
  /** Prefix for data-testid: create-order-line-items-mobile / edit-… */
  testIdPrefix: "create" | "edit";
  setQuantity: (lineKey: string, value: number) => void;
  setLineQtyMode: (lineKey: string, mode: QtyMode) => void;
  setSelectedPackSize: (lineKey: string, size: number) => void;
  updateQuantity: (lineKey: string, delta: number) => void;
  removeLineItem: (lineKey: string) => void;
  /** Discount mode; per-line discount controls show only in 'line'. */
  discountScope?: OrderDiscountScope;
  setUnitPrice?: (lineKey: string, value: number) => void;
  setLineDiscountType?: (lineKey: string, type: OrderFormDiscountType) => void;
  setLineDiscountValue?: (lineKey: string, value: number) => void;
}

export function OrderLineItems({
  lineItems,
  orderTotal,
  testIdPrefix,
  setQuantity,
  setLineQtyMode,
  setSelectedPackSize,
  updateQuantity,
  removeLineItem,
  discountScope = "none",
  setUnitPrice,
  setLineDiscountType,
  setLineDiscountValue,
}: OrderLineItemsProps) {
  const { t } = useTranslation("orders");
  const mobileTestId = `${testIdPrefix}-order-line-items-mobile`;
  const desktopTestId = `${testIdPrefix}-order-line-items-desktop`;
  const lineMode = discountScope === "line" && !!setLineDiscountType && !!setLineDiscountValue;

  if (lineItems.length === 0) {
    return <p className="text-neutral-500 py-2">{t("form_add_products_above")}</p>;
  }

  const qtyBlock = (l: OrderLineItemRow) =>
    l.sales_pack_sizes != null && l.sales_pack_sizes.length >= 1 ? (
      <LineItemQtyWithUnitsPacks
        line={l}
        setQuantity={setQuantity}
        setLineQtyMode={setLineQtyMode}
        setSelectedPackSize={setSelectedPackSize}
        updateQuantity={updateQuantity}
      />
    ) : (
      <SimpleQtyControls line={l} setQuantity={setQuantity} updateQuantity={updateQuantity} />
    );

  return (
    <>
      <ul className="md:hidden space-y-3 list-none p-0 m-0" data-testid={mobileTestId}>
        {lineItems.map((l) => (
          <li
            key={l.lineKey}
            className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-3 space-y-3"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-neutral-900 dark:text-neutral-100">{l.name}</p>
                {l.available != null && l.quantity > l.available && (
                  <p className="text-sm text-amber-600 dark:text-amber-400">{t("form_exceed_stock")}</p>
                )}
                {!setUnitPrice && (
                  <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-0.5">
                    {t("form_label_unit_price", { price: formatCurrency(l.unit_price) })}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeLineItem(l.lineKey)}
                className="shrink-0 p-2 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label={`${t("form_remove_line")} — ${l.name}`}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
            {setUnitPrice && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 shrink-0">
                    {t("form_col_unit_price")}
                  </span>
                  <UnitPriceInput line={l} setUnitPrice={setUnitPrice} />
                </div>
                <PriceUnitHint line={l} />
              </div>
            )}
            {qtyBlock(l)}
            {lineMode && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 shrink-0">
                  {t("form_col_discount")}
                </span>
                <LineDiscountControl
                  line={l}
                  setLineDiscountType={setLineDiscountType!}
                  setLineDiscountValue={setLineDiscountValue!}
                />
              </div>
            )}
            <p className="text-end font-semibold text-neutral-900 dark:text-neutral-100">
              {t("form_label_line_total", { total: formatCurrency(lineNetTotal(l, discountScope)) })}
            </p>
          </li>
        ))}
        <li className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 flex justify-end list-none">
          <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {t("form_order_total", { total: formatCurrency(orderTotal) })}
          </p>
        </li>
      </ul>

      <div
        className="hidden md:block rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden"
        data-testid={desktopTestId}
      >
        <table className="w-full text-left">
          <thead className="bg-neutral-100 dark:bg-neutral-800">
            <tr>
              <th className="px-4 py-3 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                {t("form_col_product")}
              </th>
              <th className="px-4 py-3 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                {t("form_col_unit_price")}
              </th>
              <th className="px-4 py-3 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                {t("form_col_qty")}
              </th>
              {lineMode && (
                <th className="px-4 py-3 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                  {t("form_col_discount")}
                </th>
              )}
              <th className="px-4 py-3 text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                {t("form_col_line_total")}
              </th>
              <th className="px-4 py-3 w-10" aria-hidden />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
            {lineItems.map((l) => (
              <tr key={l.lineKey}>
                <td className="px-4 py-3">
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">{l.name}</p>
                  {l.available != null && l.quantity > l.available && (
                    <p className="text-sm text-amber-600 dark:text-amber-400">{t("form_exceed_stock")}</p>
                  )}
                </td>
                <td className="px-4 py-3 text-neutral-700 dark:text-neutral-300">
                  {setUnitPrice ? (
                    <div className="flex flex-col gap-1">
                      <UnitPriceInput line={l} setUnitPrice={setUnitPrice} />
                      <PriceUnitHint line={l} />
                    </div>
                  ) : (
                    formatCurrency(l.unit_price)
                  )}
                </td>
                <td className="px-4 py-3">{qtyBlock(l)}</td>
                {lineMode && (
                  <td className="px-4 py-3">
                    <LineDiscountControl
                      line={l}
                      setLineDiscountType={setLineDiscountType!}
                      setLineDiscountValue={setLineDiscountValue!}
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-medium">
                  {formatCurrency(lineNetTotal(l, discountScope))}
                </td>
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => removeLineItem(l.lineKey)}
                    className="p-2 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 min-h-[44px] min-w-[44px] flex items-center justify-center"
                    aria-label={`${t("form_remove_line")} — ${l.name}`}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-800/50 border-t border-neutral-200 dark:border-neutral-700 flex justify-end">
          <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {t("form_order_total", { total: formatCurrency(orderTotal) })}
          </p>
        </div>
      </div>
    </>
  );
}

/** Section header + description + line items list (shared by Create/Edit). */
export function OrderLineItemsSection(props: OrderLineItemsProps) {
  const { t } = useTranslation("orders");
  return (
    <div className="w-full">
      <label className="block text-base font-semibold text-neutral-800 dark:text-neutral-200 mb-1">
        {t("form_order_lines")}
      </label>
      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">
        <Trans
          i18nKey="form_order_lines_description"
          ns="orders"
          components={{ 1: <strong />, 2: <strong /> }}
        />
      </p>
      <OrderLineItems {...props} />
    </div>
  );
}
